import { createSelector, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '@ui/app/store';
import type { DbExplorerState, SchemaBrowserViewMode } from './types';

const initialState: DbExplorerState = {
    selectedSchema: null,
    selectedTable: null,
    searchTerm: '',
    schemaFilter: null,
    viewMode: 'browse',
    schemaBrowserViewMode: 'list',
    isConfigModalOpen: false,
    editingConfigId: null,
    isLoadingTables: false,
    isLoadingTableContent: false,
    isExecutingQuery: false,
    tablesError: null,
    tableContentError: null,
    queryError: null,
};

export const dbExplorerSlice = createSlice({
    name: 'dbExplorer',
    initialState,
    reducers: {
        // Database selection
        selectDatabase: state => {
            // Reset table selection, search, and schema filter when database changes
            state.selectedSchema = null;
            state.selectedTable = null;
            state.searchTerm = '';
            state.schemaFilter = null;
        },

        // Table selection
        selectTable: (state, action: PayloadAction<{ schema: string; table: string }>) => {
            state.selectedSchema = action.payload.schema;
            state.selectedTable = action.payload.table;
        },

        clearTableSelection: state => {
            state.selectedSchema = null;
            state.selectedTable = null;
        },

        // Search
        setSearchTerm: (state, action: PayloadAction<string>) => {
            state.searchTerm = action.payload;
        },

        // Schema filter
        setSchemaFilter: (state, action: PayloadAction<string | null>) => {
            state.schemaFilter = action.payload;
        },

        // View mode
        setViewMode: (state, action: PayloadAction<'browse' | 'query'>) => {
            state.viewMode = action.payload;
        },

        // Schema browser view mode
        setSchemaBrowserViewMode: (state, action: PayloadAction<SchemaBrowserViewMode>) => {
            state.schemaBrowserViewMode = action.payload;
        },

        // Modal management
        openConfigModal: (state, action: PayloadAction<string | null>) => {
            state.isConfigModalOpen = true;
            state.editingConfigId = action.payload;
        },

        closeConfigModal: state => {
            state.isConfigModalOpen = false;
            state.editingConfigId = null;
        },

        // Loading states
        setLoadingTables: (state, action: PayloadAction<boolean>) => {
            state.isLoadingTables = action.payload;
        },

        setLoadingTableContent: (state, action: PayloadAction<boolean>) => {
            state.isLoadingTableContent = action.payload;
        },

        setExecutingQuery: (state, action: PayloadAction<boolean>) => {
            state.isExecutingQuery = action.payload;
        },

        // Error states
        setTablesError: (state, action: PayloadAction<string | null>) => {
            state.tablesError = action.payload;
        },

        setTableContentError: (state, action: PayloadAction<string | null>) => {
            state.tableContentError = action.payload;
        },

        setQueryError: (state, action: PayloadAction<string | null>) => {
            state.queryError = action.payload;
        },

        // Reset state
        resetDbExplorer: () => initialState,
    },
});

// Actions
export const {
    selectDatabase,
    selectTable,
    clearTableSelection,
    setSearchTerm,
    setSchemaFilter,
    setViewMode,
    setSchemaBrowserViewMode,
    openConfigModal,
    closeConfigModal,
    setLoadingTables,
    setLoadingTableContent,
    setExecutingQuery,
    setTablesError,
    setTableContentError,
    setQueryError,
    resetDbExplorer,
} = dbExplorerSlice.actions;

// Base selectors (primitive values, no memoization needed)
const selectSchema = (state: RootState) => state.dbExplorer.selectedSchema;
const selectTableName = (state: RootState) => state.dbExplorer.selectedTable;
export const selectSearchTerm = (state: RootState) => state.dbExplorer.searchTerm;
export const selectSchemaFilter = (state: RootState) => state.dbExplorer.schemaFilter;
const selectIsConfigModalOpen = (state: RootState) => state.dbExplorer.isConfigModalOpen;
const selectEditingConfigId = (state: RootState) => state.dbExplorer.editingConfigId;
const selectIsLoadingTables = (state: RootState) => state.dbExplorer.isLoadingTables;
const selectIsLoadingTableContent = (state: RootState) => state.dbExplorer.isLoadingTableContent;
const selectIsExecutingQuery = (state: RootState) => state.dbExplorer.isExecutingQuery;
const selectTablesError = (state: RootState) => state.dbExplorer.tablesError;
const selectTableContentError = (state: RootState) => state.dbExplorer.tableContentError;
const selectQueryError = (state: RootState) => state.dbExplorer.queryError;

export const selectSelectedTable = createSelector([selectSchema, selectTableName], (schema, table) => ({
    schema,
    table,
}));

export const selectViewMode = (state: RootState) => state.dbExplorer.viewMode;

export const selectSchemaBrowserViewMode = (state: RootState) => state.dbExplorer.schemaBrowserViewMode;

export const selectConfigModalState = createSelector(
    [selectIsConfigModalOpen, selectEditingConfigId],
    (isOpen, editingId) => ({
        isOpen,
        editingId,
    })
);

export const selectLoadingStates = createSelector(
    [selectIsLoadingTables, selectIsLoadingTableContent, selectIsExecutingQuery],
    (isLoadingTables, isLoadingTableContent, isExecutingQuery) => ({
        isLoadingTables,
        isLoadingTableContent,
        isExecutingQuery,
    })
);

export const selectErrorStates = createSelector(
    [selectTablesError, selectTableContentError, selectQueryError],
    (tablesError, tableContentError, queryError) => ({
        tablesError,
        tableContentError,
        queryError,
    })
);

export default dbExplorerSlice.reducer;
